/**
 * Pure helpers behind the Shopify Mapping tab's structured inputs (flag `structured_mapping_inputs`): they
 * read the rows of `GET /store/mapping-index` and answer "what would this typed value match?" on the client.
 */
import { normalizeMotzuSlug } from '../components/mapping/mappingModel';

export interface MappingIndexRow {
  id: string;
  name: string;
  model: string;
  brand: string;
  storeSku: string;
  categorySlugs: string[];
}

export interface KeywordRule {
  keywords: string[];
}

/**
 * Port of `matchProductTypeRule` (src/utils/shopifyTaxonomyCategory.ts): keywords are matched against the
 * lowercased name and model, any keyword wins, the first rule in list order wins. The index rows carry the model,
 * so the client probes name and model exactly as the server does; `mappingIndex.test` pins the logic to the server's.
 */
export function matchProductTypeRule<R extends KeywordRule>(
  product: { name?: string; model?: string },
  rules: R[] | undefined,
): R | null {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  const probe = `${product?.name || ''} ${product?.model || ''}`.toLowerCase();
  for (const rule of rules) {
    if (rule.keywords.some((kw) => probe.includes(kw.toLowerCase()))) return rule;
  }
  return null;
}

/** How many products each rule wins (first match wins, so a later rule counts only what earlier ones missed). */
export function ruleMatchCounts<R extends KeywordRule>(rows: MappingIndexRow[], rules: R[]): number[] {
  const counts = rules.map(() => 0);
  for (const row of rows) {
    const winner = matchProductTypeRule({ name: row.name, model: row.model }, rules);
    if (winner) counts[rules.indexOf(winner)] += 1;
  }
  return counts;
}

const STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'pro', 'new', 'set', 'kit', 'pack', 'black', 'white', 'silver', 'gray', 'grey',
  'red', 'blue', 'green', 'inch', 'mark', 'ver', 'version', 'edition', 'only', 'body', 'size',
]);

export const KEYWORD_SUGGESTION_LIMIT = 30;

/**
 * The most frequent words in the names of products no rule matches yet, minus words already used as a keyword.
 * Lower-case, at least 3 characters, no stopwords or pure numbers; each name counts a word once.
 */
export function suggestKeywords<R extends KeywordRule>(rows: MappingIndexRow[], rules: R[]): string[] {
  const used = new Set(rules.flatMap((rule) => rule.keywords.map((kw) => kw.trim().toLowerCase())).filter(Boolean));
  const freq = new Map<string, number>();
  for (const row of rows) {
    if (matchProductTypeRule({ name: row.name, model: row.model }, rules)) continue;
    const words = new Set(row.name.toLowerCase().split(/[^a-z0-9]+/));
    words.forEach((word) => {
      if (word.length < 3 || /^\d+$/.test(word) || STOPWORDS.has(word) || used.has(word)) return;
      freq.set(word, (freq.get(word) || 0) + 1);
    });
  }
  return Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, KEYWORD_SUGGESTION_LIMIT)
    .map(([word]) => word);
}

/** Products per normalized catalog category slug present in the selection. */
export function categorySlugCounts(rows: MappingIndexRow[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const own = new Set(row.categorySlugs.map(normalizeMotzuSlug).filter(Boolean));
    own.forEach((slug) => counts.set(slug, (counts.get(slug) || 0) + 1));
  }
  return counts;
}

/** Same rule as the registry lookup (merchantTaxonomyService): the SKU equals the prefix or starts with `prefix-`, case-insensitive. */
export function skuMatchesPrefix(storeSku: string, prefix: string): boolean {
  const p = prefix.trim().toLowerCase();
  if (!p) return false;
  const sku = storeSku.trim().toLowerCase();
  return sku === p || sku.startsWith(`${p}-`);
}

export const countPrefixMatches = (rows: MappingIndexRow[], prefix: string): number =>
  rows.reduce((n, row) => n + (skuMatchesPrefix(row.storeSku, prefix) ? 1 : 0), 0);

/** Distinct leading `AAA-123` segments of store SKUs that no existing prefix already claims, commonest first. */
export function suggestSkuPrefixes(rows: MappingIndexRow[], claimed: string[]): Array<{ prefix: string; count: number }> {
  const taken = claimed.map((p) => p.trim()).filter(Boolean);
  const counts = new Map<string, { prefix: string; count: number }>();
  for (const row of rows) {
    const m = /^([A-Za-z]+-\d+)(?:-|$)/.exec(row.storeSku.trim());
    if (!m) continue;
    if (taken.some((p) => skuMatchesPrefix(row.storeSku, p))) continue;
    const key = m[1].toLowerCase();
    const hit = counts.get(key);
    if (hit) hit.count += 1;
    else counts.set(key, { prefix: m[1], count: 1 });
  }
  return Array.from(counts.values()).sort((a, b) => b.count - a.count || a.prefix.localeCompare(b.prefix));
}
